// The result bar chart on country and election pages (Chart.js, tree-shaken to a
// bar chart). A horizontal bar per party or candidate, labelled on the axis, so the
// chart never depends on colour alone; the table under it has the same figures and
// is the accessible version. Drawn only when the chart scrolls into view.
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js"

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip)

// Categorical fallback for a party with no colour on record: a ColorBrewer-style
// set that stays distinguishable under the common colour-vision deficiencies.
const PALETTE = ["#0072b2", "#e69f00", "#009e73", "#cc79a7", "#56b4e9", "#d55e00", "#f0e442", "#666666", "#332288", "#88ccee"]

function css(name, fallback) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

function draw(canvas) {
    const data = JSON.parse(canvas.dataset.ekChart)
    const ink = css("--ek-muted", "#56607a")
    const grid = css("--ek-line", "#dcdfe8")
    const unit = data.unit
    const format = (value) => (unit === "share" ? `${value}%` : value.toLocaleString())

    canvas.parentElement.style.height = `${Math.max(180, data.labels.length * 34 + 48)}px`
    // The figure at the end of each bar, so the chart can be read against the table.
    const valueLabels = {
        id: "valueLabels",
        afterDatasetsDraw(chart) {
            const { ctx } = chart
            ctx.save()
            ctx.fillStyle = css("--ek-ink", "#0f1a33")
            ctx.font = "600 12px system-ui, sans-serif"
            ctx.textBaseline = "middle"
            chart.getDatasetMeta(0).data.forEach((bar, index) => ctx.fillText(format(data.values[index]), bar.x + 6, bar.y))
            ctx.restore()
        }
    }

    new Chart(canvas, {
        plugins: [valueLabels],
        type: "bar",
        data: {
            labels: data.labels,
            datasets: [{
                data: data.values,
                backgroundColor: data.colors.map((color, index) => color || PALETTE[index % PALETTE.length]),
                borderRadius: 3
            }]
        },
        options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            // No animation: a bar caught half-grown reads as a number it is not.
            animation: false,
            layout: { padding: { right: 56 } },
            plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: (item) => `${format(item.parsed.x)}${unit === "seats" ? " seats" : ""}` } }
            },
            scales: {
                x: { beginAtZero: true, grid: { color: grid }, ticks: { color: ink, callback: (value) => format(value) } },
                y: { grid: { display: false }, ticks: { color: ink, autoSkip: false } }
            }
        }
    })
}

const canvases = document.querySelectorAll("canvas[data-ek-chart]")
if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue
            observer.unobserve(entry.target)
            draw(entry.target)
        }
    }, { rootMargin: "200px" })
    canvases.forEach((canvas) => observer.observe(canvas))
} else {
    canvases.forEach(draw)
}
